import * as React from "react";
import {
    FloatingFocusManager,
    FloatingPortal,
    Placement,
    useId,
    useMergeRefs,
} from "@floating-ui/react";
import { clsx } from "clsx";
import { assertNotNully } from "@/util/typeguards.ts";
import {
    PopoverContext as PopoverContext1,
    usePopover,
    usePopoverContext,
} from "@/common/Popover/hooks.tsx";

export interface PopoverOptions {
    initialOpen?: boolean;
    placement?: Placement;
    modal?: boolean;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    autoUpdatePosition?: boolean;
    interactionKind?: "click" | "hover";
}

export function Popover({
    children,
    modal = false,
    ...restOptions
}: {
    children:
        | React.ReactNode
        | ((popover: ReturnType<typeof usePopover>) => React.ReactNode);
} & PopoverOptions) {
    // This can accept any props as options, e.g. `placement`,
    // or other positioning options.
    const popover = usePopover({ modal, ...restOptions });
    return (
        <PopoverContext1 value={popover}>
            {typeof children === "function" ? children(popover) : children}
        </PopoverContext1>
    );
}

interface PopoverTriggerProps {
    children: React.ReactNode;
    asChild?: boolean;
    ref?: React.Ref<HTMLElement>;
}

export function PopoverTrigger({
    children,
    asChild = false,
    ref: propRef,
    ...props
}: PopoverTriggerProps) {
    const context = usePopoverContext();
    assertNotNully(children);

    const ref = useMergeRefs([context.refs.setReference, propRef]);

    // `asChild` allows the user to pass any element as the anchor
    if (asChild && React.isValidElement(children)) {
        return React.cloneElement(
            children,
            context.getReferenceProps({
                ref,
                ...props,
                // @ts-expect-error - Don't know what would happen if this is removed
                ...children.props,
                "data-state": context.open ? "open" : "closed",
            }),
        );
    }

    return (
        <button
            ref={ref}
            type="button"
            // The controls' radius, so its focus ring follows what it wraps
            className="rounded-lg"
            // The user can style the trigger based on the state
            data-state={context.open ? "open" : "closed"}
            {...context.getReferenceProps(props)}
        >
            {children}
        </button>
    );
}

interface PopoverContentProps {
    darkRingInset?: boolean;
}

export const PopoverContent = React.forwardRef<
    HTMLDivElement,
    React.HTMLProps<HTMLDivElement> & PopoverContentProps
>(function PopoverContent(
    { style, className, darkRingInset = true, ...props },
    propRef,
) {
    const { context: floatingContext, ...context } = usePopoverContext();
    const ref = useMergeRefs([context.refs.setFloating, propRef]);

    if (!floatingContext.open) return null;

    return (
        <FloatingPortal>
            <FloatingFocusManager
                context={floatingContext}
                modal={context.modal}
                closeOnFocusOut={false}
            >
                <div
                    ref={ref}
                    style={{ ...context.floatingStyles, ...style }}
                    aria-labelledby={context.labelId}
                    aria-describedby={context.descriptionId}
                    {...context.getFloatingProps(props)}
                    className={clsx(
                        className,
                        "max-w-[20rem]",
                        "text-sm",
                        // Anchor positioning
                        "[--anchor-gap:theme(spacing.2)] [--anchor-padding:theme(spacing.1)] data-[anchor~=end]:[--anchor-offset:6px] data-[anchor~=start]:[--anchor-offset:-6px] sm:data-[anchor~=end]:[--anchor-offset:4px] sm:data-[anchor~=start]:[--anchor-offset:-4px]",
                        // Base styles
                        "isolate w-max rounded-xl",
                        // Invisible border that is only visible in `forced-colors` mode for accessibility purposes
                        "outline outline-1 outline-transparent focus:outline-none",
                        // Handle scrolling when menu won't fit in viewport
                        "overflow-y-auto",
                        // Popover background
                        "bg-white/75 backdrop-blur-xl dark:bg-zinc-800/75",
                        // Shadows
                        "shadow-lg ring-1 ring-zinc-950/10 dark:ring-white/10",
                        darkRingInset && "dark:ring-inset",
                    )}
                >
                    {props.children}
                </div>
            </FloatingFocusManager>
        </FloatingPortal>
    );
});

export const PopoverHeading = React.forwardRef<
    HTMLHeadingElement,
    React.HTMLProps<HTMLHeadingElement>
>(function PopoverHeading(props, ref) {
    const { setLabelId } = usePopoverContext();
    const id = useId();

    // Only sets `aria-labelledby` on the Popover root element
    // if this component is mounted inside it.
    React.useLayoutEffect(() => {
        setLabelId(id);
        return () => setLabelId(undefined);
    }, [id, setLabelId]);

    return (
        <h2 {...props} ref={ref} id={id}>
            {props.children}
        </h2>
    );
});

export const PopoverDescription = React.forwardRef<
    HTMLParagraphElement,
    React.HTMLProps<HTMLParagraphElement>
>(function PopoverDescription(props, ref) {
    const { setDescriptionId } = usePopoverContext();
    const id = useId();

    // Only sets `aria-describedby` on the Popover root element
    // if this component is mounted inside it.
    React.useLayoutEffect(() => {
        setDescriptionId(id);
        return () => setDescriptionId(undefined);
    }, [id, setDescriptionId]);

    return <div {...props} ref={ref} id={id} />;
});

export const PopoverClose = React.forwardRef<
    HTMLButtonElement,
    React.ButtonHTMLAttributes<HTMLButtonElement>
>(function PopoverClose(props, ref) {
    const { setOpen } = usePopoverContext();
    return (
        <button
            type="button"
            ref={ref}
            {...props}
            onClick={(event) => {
                props.onClick?.(event);
                setOpen(false);
            }}
        />
    );
});
